import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import { CompanyVendor, SignedDocument, User } from 'src/app/classes/models';
import { File as FileModel } from 'src/app/classes/models/file';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { SignedDocumentService } from 'src/app/services/signed-document.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-signed-document-upload-modal',
  templateUrl: './signed-document-upload-modal.component.html',
  styleUrl: './signed-document-upload-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SignedDocumentUploadModalComponent implements OnInit {
  constructor(
    private activeModal: NgbActiveModal,
    private UtilityService: UtilityService,
    private ToastService: ToastService,
    private AuthService: AuthService,
    private CompanyVendorService: CompanyVendorService,
    private SignedDocumentService: SignedDocumentService,
    private cdr: ChangeDetectorRef
  ) {
    this.LoggedInUser = this.AuthService.getUser();
  }

  @Input() CompanyVendor?: CompanyVendor;
  @Input() existingSignedDocument?: SignedDocument;

  VendorAgreementFile?: FileModel;
  W9File?: FileModel;
  LoggedInUser!: User;

  approvedAdminRoles: UserAccountRoles[] = [
    'ADMIN_ADMIN',
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM',
    'ADMIN_FRAUD_TEAM'
  ];
  allVendorClients?: boolean = false;
  signedDocumentsToSave: SignedDocument[] = [];

  isAdminUser() {
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (!role) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user account role.',
        { returnError: true }
      );
    }

    return new Set(this.approvedAdminRoles).has(role);
  }

  isW9OnlyUpload(): boolean {
    return !!this.W9File && !this.VendorAgreementFile;
  }

  onAllVendorClientsChange() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload file(s). No Company vendor record found',
        { returnError: true }
      );
    }

    const { VendorId, CompanyId } = this.CompanyVendor;

    if (this.allVendorClients) {
      // Clear any existing documents for other vendors before adding new ones
      this.signedDocumentsToSave = [];

      this.CompanyVendorService.getCompanyVendors(
        this.UtilityService.stringifyObjectsInQueryObject({
          attributes: [
            'id',
            'VendorId',
            'CompanyId',
            'ExemptVendorId',
            'ClientRegisteredVendorId'
          ],
          VendorId,
          CompanyId: { $ne: CompanyId },
          Company: {
            options: { required: false },
            attributes: ['id', 'legalName']
          }
        })
      ).subscribe({
        next: (CompanyVendors: CompanyVendor[]) => {
          CompanyVendors.forEach((cv) => {
            this.createSignedDocumentForCompanyVendor(cv);
          });
          this.cdr.markForCheck();
        }
      });
    } else {
      this.signedDocumentsToSave = this.signedDocumentsToSave.filter(
        (doc) => doc.CompanyId === CompanyId
      );
      this.cdr.markForCheck();
    }
  }

  ngOnInit() {
    if (!this.isAdminUser()) {
      throw this.ToastService.addErrorToast(
        'Error',
        'You do not have permission to access this feature.',
        { returnError: true }
      );
    }

    if (this.existingSignedDocument) {
      this.loadExistingFiles();
    }
  }

  loadExistingFiles() {
    if (!this.existingSignedDocument?.id) {
      return;
    }

    const query = {
      id: this.existingSignedDocument.id,
      SignedDocumentFile: {
        options: { required: false },
        File: {
          options: { required: false },
          attributes: ['id', 'filename', 'filesize', 'filetype', 'filepath']
        }
      }
    };

    this.SignedDocumentService.getSignedDocument(
      this.UtilityService.stringifyObjectsInQueryObject(query)
    ).subscribe({
      next: (signedDocument: SignedDocument) => {
        const { SignedDocumentFiles = [] } = signedDocument;

        const unassignedFiles: FileModel[] = [];

        // First pass: assign files that clearly match W9 or vendor agreement patterns
        SignedDocumentFiles.forEach(({ File }) => {
          if (!File || !File.filename) return;

          const { filename, filesize, filetype } = File;

          const existingFile = {
            id: File.id,
            filename,
            filesize,
            filetype
          };

          // Check if this is a W9 or vendor agreement based on filename patterns
          const isW9 =
            filename.toLowerCase().includes('w9') ||
            filename.toLowerCase().includes('w-9');
          const isVendorAgreement =
            filename.toLowerCase().includes('vendor') ||
            filename.toLowerCase().includes('agreement');

          if (isW9 && !this.W9File) {
            this.W9File = existingFile;
          } else if (isVendorAgreement && !this.VendorAgreementFile) {
            this.VendorAgreementFile = existingFile;
          } else {
            // Neither pattern matched clearly, add to unassigned list
            unassignedFiles.push(existingFile);
          }
        });

        // Second pass: assign remaining unassigned files in order to empty slots
        unassignedFiles.forEach((file) => {
          if (!this.VendorAgreementFile) {
            this.VendorAgreementFile = file;
          } else if (!this.W9File) {
            this.W9File = file;
          }
        });

        this.cdr.markForCheck();
      },
      error: (error) => {
        this.ToastService.addErrorToast(
          'Error',
          'Failed to load existing document files.'
        );
      }
    });
  }

  removeVendorAgreementFile({ index }: { File: FileModel; index: number }) {
    this.VendorAgreementFile = undefined;
    this.cdr.markForCheck();
  }

  removeW9File({ index }: { File: FileModel; index: number }) {
    this.W9File = undefined;
    this.cdr.markForCheck();
  }

  vendorAgreementFileUpload({
    data: File
  }: {
    data: File;
    type?: null | 'No Scan';
  }) {
    this.processFileUpload(File, 'vendorAgreement');
  }

  w9FileUpload({ data: File }: { data: File; type?: null | 'No Scan' }) {
    this.processFileUpload(File, 'w9');
  }

  private processFileUpload(
    File: File,
    documentType: 'vendorAgreement' | 'w9'
  ) {
    const { name: filename, size: filesize, type: filetype } = File;

    from(File.arrayBuffer()).subscribe({
      next: (FileArrayBuffer) => {
        const FileString = `data:${filetype};base64,${this.UtilityService.convertArrayBufferToBase64EncodedString(FileArrayBuffer)}`;

        const uploadedFile = {
          filename,
          filesize,
          filetype,
          FileContent: {
            filecontents: FileString
          }
        };

        // Replace the existing file (whether it's an existing S3 file or a new upload)
        if (documentType === 'vendorAgreement') {
          this.VendorAgreementFile = uploadedFile;
          // Reset all clients checkbox since it's only available for W9-only uploads
          this.allVendorClients = false;
          this.signedDocumentsToSave = [];
        } else {
          this.W9File = uploadedFile;
        }

        this.cdr.markForCheck();
      },
      error: (error) => {
        this.ToastService.addErrorToast(
          'Error',
          'Failed to process file upload.'
        );
      }
    });
  }

  createSignedDocumentForCompanyVendor(companyVendor: CompanyVendor) {
    const { id, CompanyId, VendorId } = companyVendor;

    if (!this.W9File?.FileContent?.filecontents) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload W9. No file found.',
        { returnError: true }
      );
    }

    if (!CompanyId || !id || !VendorId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload W9. Missing required company vendor info.',
        { returnError: true }
      );
    }

    const signedDocument = new SignedDocument({});

    Object.assign(signedDocument, {
      CompanyId,
      CompanyVendorId: id,
      VendorId,
      service: 'Manual Upload',
      name: 'W9',
      description: `W9 for ${this.CompanyVendor?.Vendor?.legalName || ''}`,
      Files: [this.W9File],
      signedDate: new Date()
    });

    this.signedDocumentsToSave.push(signedDocument);
  }

  createSignedDocuments(companyVendor: CompanyVendor): SignedDocument[] {
    const { CompanyId, VendorId, ExemptVendorId, ClientRegisteredVendorId } =
      companyVendor;

    if (
      !CompanyId ||
      (!VendorId && !ExemptVendorId && !ClientRegisteredVendorId)
    ) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload signed document. Missing required info.',
        { returnError: true }
      );
    }

    // Collect ALL files that should be present (both new uploads and existing files to keep)
    const allFiles: FileModel[] = [];

    if (this.existingSignedDocument) {
      if (this.VendorAgreementFile) {
        allFiles.push(this.VendorAgreementFile);
      }
      if (this.W9File) {
        allFiles.push(this.W9File);
      }
    } else {
      if (this.VendorAgreementFile?.FileContent?.filecontents) {
        allFiles.push(this.VendorAgreementFile);
      }
      if (this.W9File?.FileContent?.filecontents) {
        allFiles.push(this.W9File);
      }
    }

    // Generate name and description based on uploaded files
    const hasVendorAgreement = !!this.VendorAgreementFile;
    const hasW9 = !!this.W9File;

    let name: string;
    if (hasVendorAgreement && hasW9) {
      name = 'Vendor Agreement & W9';
    } else if (hasVendorAgreement) {
      name = 'Vendor Agreement';
    } else {
      name = 'W9';
    }

    if (!companyVendor.Company?.legalName) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing company information.',
        { returnError: true }
      );
    }

    const description = `${name} for ${companyVendor.Company.legalName}`;

    const signedDocument = new SignedDocument({});

    if (this.existingSignedDocument?.id) {
      Object.assign(signedDocument, {
        id: this.existingSignedDocument.id,
        name,
        description,
        Files: allFiles
      });
    } else {
      Object.assign(signedDocument, {
        CompanyId,
        VendorId,
        ExemptVendorId,
        ClientRegisteredVendorId,
        service: 'Manual Upload',
        name,
        description,
        Files: allFiles,
        signedDate: new Date()
      });
    }

    return [signedDocument];
  }

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  upload() {
    const hasVendorAgreement = !!this.VendorAgreementFile;
    const hasW9 = !!this.W9File;

    // In edit mode, we might not have new files to upload (just updating existing)
    if (!this.existingSignedDocument && !hasVendorAgreement && !hasW9) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload file(s). No files found to upload.',
        { returnError: true }
      );
    }

    // In edit mode, at least one file should exist (new or existing)
    if (this.existingSignedDocument && !hasVendorAgreement && !hasW9) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot update signed document. At least one file must be present.',
        { returnError: true }
      );
    }

    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot upload file(s). No Company vendor record found',
        { returnError: true }
      );
    }

    const currentSignedDocuments = this.createSignedDocuments(
      this.CompanyVendor
    );

    const allSignedDocuments = [
      ...currentSignedDocuments,
      ...this.signedDocumentsToSave
    ];

    this.activeModal.close(allSignedDocuments);
  }
}
